import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import PageLayout from '../components/layout/PageLayout';
import { statsApi } from '../lib/api';
import { queryKeys } from '../lib/queryKeys';

const F: React.CSSProperties = { fontFamily: 'Inter, sans-serif' };

const CARD: React.CSSProperties = {
  background: '#111',
  border: '1px solid rgba(255,255,255,0.06)',
  borderRadius: 14,
  padding: 20,
};

function formatSigned(value: number, suffix = '') {
  const signal = value > 0 ? '+' : '';
  return `${signal}${value.toFixed(1)}${suffix}`;
}

function formatDate(value?: string) {
  if (!value) return 'Sem dados';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

export default function Stats() {
  const { data, isLoading, isError } = useQuery({
    queryKey: queryKeys.stats,
    queryFn: () => statsApi.overview(),
  });

  const statCards = [
    {
      label: 'EV TOTAL',
      value: data ? formatSigned(data.total_ev_bb, ' BB') : '--',
      accent: data && data.total_ev_bb >= 0 ? '#00e676' : '#e53935',
    },
    {
      label: 'SESSÕES',
      value: data ? String(data.sessions_count) : '--',
      accent: '#fff',
    },
    {
      label: 'MÉDIA DE MÃOS',
      value: data ? Math.round(data.avg_hands).toString() : '--',
      accent: '#fff',
    },
    {
      label: 'WINRATE',
      value: data ? formatSigned(data.win_rate, ' BB/100') : '--',
      accent: data && data.win_rate >= 0 ? '#00e676' : '#e53935',
    },
  ];

  return (
    <PageLayout activeRoute="/stats">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 20, ...F }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-end',
            gap: 16,
          }}
        >
          <div>
            <p
              style={{
                color: 'rgba(255,255,255,0.35)',
                fontSize: 12,
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                margin: 0,
              }}
            >
              Visão geral
            </p>
            <h1
              style={{
                color: '#fff',
                fontSize: 28,
                fontWeight: 800,
                margin: '8px 0 4px',
              }}
            >
              Estatísticas
            </h1>
            <p style={{ color: 'rgba(255,255,255,0.42)', fontSize: 14, margin: 0 }}>
              Resumo consolidado das suas sessões analisadas.
            </p>
          </div>

          <Link
            to="/sessions"
            style={{
              textDecoration: 'none',
              background: 'linear-gradient(135deg,#00E633,#009BFF)',
              color: '#000',
              fontWeight: 800,
              fontSize: 13,
              padding: '10px 18px',
              borderRadius: 10,
            }}
          >
            Ver sessões
          </Link>
        </div>

        {isLoading ? (
          <div style={{ ...CARD, color: 'rgba(255,255,255,0.55)' }}>
            Carregando estatísticas...
          </div>
        ) : isError || !data ? (
          <div style={{ ...CARD, display: 'flex', flexDirection: 'column', gap: 10 }}>
            <h2 style={{ color: '#fff', fontSize: 18, margin: 0 }}>
              Não foi possível carregar as estatísticas
            </h2>
            <p style={{ color: 'rgba(255,255,255,0.42)', fontSize: 14, margin: 0 }}>
              Assim que o backend responder com o overview, esta página mostra o
              consolidado automaticamente.
            </p>
            <Link
              to="/sessions"
              style={{
                color: '#00e676',
                textDecoration: 'none',
                fontWeight: 700,
                width: 'fit-content',
              }}
            >
              Ir para análise de mãos
            </Link>
          </div>
        ) : (
          <>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
                gap: 16,
              }}
            >
              {statCards.map((card) => (
                <div key={card.label} style={CARD}>
                  <p
                    style={{
                      color: 'rgba(255,255,255,0.35)',
                      fontSize: 11,
                      letterSpacing: '0.14em',
                      textTransform: 'uppercase',
                      margin: 0,
                    }}
                  >
                    {card.label}
                  </p>
                  <div
                    style={{
                      color: card.accent,
                      fontSize: 28,
                      fontWeight: 800,
                      marginTop: 10,
                    }}
                  >
                    {card.value}
                  </div>
                </div>
              ))}
            </div>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                gap: 16,
              }}
            >
              <div style={CARD}>
                <p
                  style={{
                    color: 'rgba(255,255,255,0.35)',
                    fontSize: 11,
                    letterSpacing: '0.14em',
                    textTransform: 'uppercase',
                    margin: 0,
                  }}
                >
                  Melhor sessão
                </p>
                <div style={{ color: '#00e676', fontSize: 24, fontWeight: 800, marginTop: 10 }}>
                  {formatSigned(data.best_session?.ev ?? 0, ' BB')}
                </div>
                <p style={{ color: 'rgba(255,255,255,0.42)', fontSize: 14, margin: '8px 0 0' }}>
                  {formatDate(data.best_session?.date)}
                </p>
              </div>

              <div style={CARD}>
                <p
                  style={{
                    color: 'rgba(255,255,255,0.35)',
                    fontSize: 11,
                    letterSpacing: '0.14em',
                    textTransform: 'uppercase',
                    margin: 0,
                  }}
                >
                  Pior sessão
                </p>
                <div style={{ color: '#e53935', fontSize: 24, fontWeight: 800, marginTop: 10 }}>
                  {formatSigned(data.worst_session?.ev ?? 0, ' BB')}
                </div>
                <p style={{ color: 'rgba(255,255,255,0.42)', fontSize: 14, margin: '8px 0 0' }}>
                  {formatDate(data.worst_session?.date)}
                </p>
              </div>
            </div>
          </>
        )}
      </div>
    </PageLayout>
  );
}
